// Zajednicka logika za stranice liste oglasa (pages/oglasi/**/[page].vue):
// podaci iz API-ja, paginacija, 404 za nepostojecu kategoriju/mesto, noindex za praznu listu,
// naslov / opis / h1, JSON-LD (CollectionPage + BreadcrumbList) i <link rel="prev|next">.
//
// Korisenje (svaka stranica kaze koji delovi rute postoje):
//   const { loaded, h1, list, empty } = await useAdListing({ place: true, category: true })

import { usePaginationStore } from '~/stores/pagination'
import { useCustomerLoginStore } from '~/stores/login/customer'
import { useCustomerItemsStore } from '~/stores/customer/items'
import { useShareMetaStore } from '~/stores/shareMeta'
import FilterDataByRoute from '~/components/cmsCustomer/advertisement/utils/FilterDataByRoute'

interface AdListingOptions {
  place?: boolean
  category?: boolean
  subcategory?: boolean
}

const SITE = 'https://pijace.com'
const DEFAULT_DESCRIPTION = 'Oglasi na pijaci, kupovina i prodaja stoke, žitarica, voća i povrća na pijacama.'

const lcFirst = (text?: string | null) => (text ? text.charAt(0).toLowerCase() + text.slice(1) : '')

export async function useAdListing(options: AdListingOptions = {}) {
  const route = useRoute()
  const { $srbTolat } = useNuxtApp() as any

  const paginationStore = usePaginationStore()
  const customerStore = useCustomerLoginStore()
  const itemsStore = useCustomerItemsStore()
  const shareMeta = useShareMetaStore()

  const { getFixed, getBuying, getType, getPage } = FilterDataByRoute()

  const page = computed(() => getPage())

  // slugovi iz rute (koriste se samo oni koje stranica ima)
  const slug = (name: string, used?: boolean) => (used ? String(route.params[name] ?? '') : '')
  const categorySlug = computed(() => slug('category', options.category))
  const subcategorySlug = computed(() => slug('subcategory', options.subcategory))
  const placeSlug = computed(() => slug('place', options.place))
  // "krusevac-pijaca" -> "krusevac pijaca"
  const placeName = computed(() => placeSlug.value.split('-').join(' '))

  const customerId = computed(() => (customerStore.loggedIn ? customerStore.customer.id : 0))

  // useAsyncData vraca i podatke i Promise: refovi su dostupni odmah, a cekanje je na kraju funkcije;
  // sve sto zove useHead / useSeoMeta mora da se izvrsi PRE prvog await-a (posle njega Nuxt kontekst nestaje)
  const asyncData = useAsyncData(
    () => `ad-${route.fullPath}-${customerId.value}`,
    () =>
      itemsStore.getCustomerItemsPaginatePublic({
        category: categorySlug.value,
        subcategory: subcategorySlug.value,
        fixed: getFixed(),
        trade: getBuying(),
        type_currency: getType(),
        page: page.value,
        place: placeName.value,
        id: customerId.value,
      }),
  )
  const { data: allAd, error, pending } = asyncData

  const data = computed<any>(() => (allAd.value as any)?.data ?? null)
  const placeObject = computed(() => data.value?.place ?? null)
  const categoryObject = computed(() => data.value?.category ?? null)
  const subcategoryObject = computed(() => data.value?.subcategory ?? null)

  const list = computed<any[]>(() => data.value?.items?.data ?? [])
  const total = computed(() => Number(data.value?.items?.total) || list.value.length)
  const lastPage = computed(() => Number(data.value?.items?.last_page) || 1)

  // paginacija u roditeljskoj stranici (pages/oglasi.vue) cita last_page iz store-a
  watch(
    allAd,
    (val: any) => {
      if (val?.data?.items) {
        paginationStore.setLastPage(val.data.items.last_page)
      }
    },
    { immediate: true },
  )

  // sve sto ruta trazi (mesto / kategorija / potkategorija) mora da postoji u odgovoru
  const entitiesReady = computed(
    () =>
      (!options.place || !!placeObject.value) &&
      (!options.category || !!categoryObject.value) &&
      (!options.subcategory || !!subcategoryObject.value),
  )

  // odgovor je stigao, ali kategorija / potkategorija / mesto ne postoje -> 404 (a ne prazna indeksirana stranica)
  const notFound = computed(() => !!data.value?.items && !entitiesReady.value)
  const notFoundError = () => createError({ statusCode: 404, statusMessage: 'Stranica nije pronađena', fatal: true })

  if (import.meta.client) {
    watch(notFound, (value) => {
      if (value) showError(notFoundError())
    })
  }

  const loaded = computed(() => !pending.value && !error.value && !!data.value?.items && entitiesReady.value)
  const empty = computed(() => loaded.value && total.value === 0)

  // ---------- SEO ----------
  const canonicalUrl = computed(() => `${SITE}${route.fullPath}`)

  // najuzi deo rute: potkategorija, pa kategorija
  const subject = computed(() => subcategoryObject.value ?? categoryObject.value)
  const hasFilters = !!(options.place || options.category || options.subcategory)

  const h1 = computed(() => {
    if (!hasFilters) return 'Oglasi na pijaci'
    if (!entitiesReady.value) return ''
    const place = placeObject.value?.name
    if (subject.value) {
      return place ? `${subject.value.name} – oglasi, ${place}` : `${subject.value.name} – oglasi`
    }
    return `Oglasi u mestu ${place}`
  })

  const headTitle = computed(() => {
    if (!hasFilters) return 'Oglasi na pijaci, kupovina i prodaja stoke | Pijace.com'
    if (!entitiesReady.value) return 'Oglasi na pijaci | Pijace.com'
    const place = placeObject.value?.name
    if (subject.value) {
      const meta = lcFirst(subject.value.description_meta)
      if (place) return `${subject.value.name} – oglasi, ${place} | Pijace.com`
      return meta ? `${subject.value.name} – ${meta} | Pijace.com` : `${subject.value.name} – oglasi | Pijace.com`
    }
    return `Oglasi u mestu ${place} | Pijace.com`
  })

  const headDescription = computed(() => {
    if (!hasFilters) return DEFAULT_DESCRIPTION
    if (subject.value) return subject.value.description_meta ?? DEFAULT_DESCRIPTION
    return placeObject.value ? `${DEFAULT_DESCRIPTION} ${placeObject.value.name}` : DEFAULT_DESCRIPTION
  })

  const ogImage = computed(() => subject.value?.img ?? null)

  const collectionLd = computed(() => {
    if (!entitiesReady.value || list.value.length === 0) return null

    const perPage = Number(data.value?.items?.per_page) || list.value.length
    // apsolutna pozicija u celom listingu
    const pageOffset = (page.value - 1) * perPage

    const itemListElement = list.value.slice(0, 10).map((ad: any, i: number) => ({
      '@type': 'ListItem',
      position: pageOffset + i + 1,
      item: {
        '@type': 'Thing',
        name: ad?.title || `Oglas ${i + 1}`,
        ...(ad?.id
          ? { url: `${SITE}/oglas/${ad.slug || $srbTolat.srbTolatPath(String(ad.title || ''))}/${ad.id}` }
          : {}),
      },
    }))

    const names = [placeObject.value, categoryObject.value, subcategoryObject.value]
      .filter(Boolean)
      .map((entity: any) => entity.name)

    return {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      '@id': `${canonicalUrl.value}#collection`,
      url: canonicalUrl.value,
      name: headTitle.value,
      description: headDescription.value,
      inLanguage: 'sr',
      isPartOf: { '@type': 'WebSite', name: 'Pijace.com', url: SITE },
      publisher: {
        '@type': 'Organization',
        name: 'Pijace.com',
        url: SITE,
        logo: { '@type': 'ImageObject', url: `${SITE}/images/pijace-60.png` },
      },
      ...(placeObject.value
        ? { spatialCoverage: { '@type': 'Place', name: placeObject.value.name, addressCountry: 'RS' } }
        : {}),
      mainEntity: {
        '@type': 'ItemList',
        name: names.length ? `Oglasi - ${names.join(' - ')}` : 'Oglasi na pijaci',
        // ukupan broj oglasa; lista sadrzi prvih 10 sa ove strane
        numberOfItems: total.value,
        itemListElement,
      },
    }
  })

  // Oglasi -> mesto -> kategorija -> potkategorija (poslednja stavka je ova stranica)
  const breadcrumbLd = computed(() => {
    if (!entitiesReady.value) return null

    const crumbs: { name: string; url: string }[] = [{ name: 'Oglasi na pijaci', url: `${SITE}/oglasi/svi-oglasi/1` }]
    const placePath = `/oglasi/mesto/${placeSlug.value}`
    const categoryPath = placeObject.value ? `${placePath}/${categorySlug.value}` : `/oglasi/${categorySlug.value}`

    if (placeObject.value) crumbs.push({ name: placeObject.value.name, url: `${SITE}${placePath}/1` })
    if (categoryObject.value) crumbs.push({ name: categoryObject.value.name, url: `${SITE}${categoryPath}/1` })
    if (subcategoryObject.value) crumbs.push({ name: subcategoryObject.value.name, url: canonicalUrl.value })

    // ova stranica je uvek poslednja, sa svojim (kanonskim) URL-om
    crumbs[crumbs.length - 1].url = canonicalUrl.value

    return {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: crumbs.map((crumb, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        name: crumb.name,
        item: crumb.url,
      })),
    }
  })

  watchEffect(() => {
    if (!entitiesReady.value) return
    shareMeta.setMeta({
      title: headTitle.value,
      description: headDescription.value,
      url: canonicalUrl.value,
    })
  })

  usePijaceSeo({
    title: headTitle,
    description: headDescription,
    url: canonicalUrl,
    type: 'website',
    image: ogImage,
    jsonLd: () => [collectionLd.value, breadcrumbLd.value],
  })

  // strana 1 indeksira, strane 2+ i prazna lista idu noindex,follow
  usePaginationRobots(page, empty)

  // dugmad paginacije nisu linkovi, pa prethodna / sledeca strana idu kao <link rel> da ih pretraga nadje;
  // filteri su u istom segmentu rute ("kupujem-cena-2"), pa se menja samo broj na kraju
  useHead(() => {
    const pageUrl = (n: number) => `${SITE}${route.path.replace(/\d+$/, String(n))}`
    const links: { rel: string; href: string }[] = []
    if (page.value > 1) links.push({ rel: 'prev', href: pageUrl(page.value - 1) })
    if (page.value < lastPage.value) links.push({ rel: 'next', href: pageUrl(page.value + 1) })
    return { link: links }
  })

  await asyncData

  // prvo ucitavanje (SSR): nepostojeca kategorija / mesto vraca pravi 404
  if (notFound.value) {
    throw notFoundError()
  }

  return { loaded, empty, h1, list }
}
